/**
 * OPERATIONAL-OVERVIEW-R3 — pure helpers for the R3 field-supervision dashboard.
 *
 * The R2 dashboard answered "what is the structure of my farm?"; R3 answers
 * "what is working right now?". These helpers are deliberately BOUNDED and
 * TRUTHFUL: they only derive a fact when the canonical backend data proves it.
 * Nothing here invents a pump→site, sensor→sector, pressure→sector, or
 * "since X minutes" association.
 *
 * Pure helpers only — no React, no network, no side effects. They consume the
 * shapes already produced by the backend and re-interpret live metrics through
 * the shared `liveSensorMetrics` layer (no metric rule is duplicated here).
 */

import type { IoTNode, NodeLatest } from './iotApi';
import {
  getValveChannels,
  getValveState,
  isValveCapable,
  type Measures,
} from './liveSensorMetrics';

/** Operational state a sector can legitimately reach with today's data.
 *
 *  R3-1 deliberately uses only the states the current decoder + canonical
 *  sector binding can PROVE. `pending` / `anomaly` exist in the type vocabulary
 *  but are not derived yet — no state is shown merely because the type exists.
 */
export type OperationalState = 'active' | 'stopped' | 'unknown' | 'not_commissioned';

/** Same 15-minute freshness window the rest of the dashboard uses. */
export const FRESHNESS_MS = 15 * 60 * 1000;

// ---------------------------------------------------------------------------
// Backend hierarchy records (fields / sectors) used to join node.sector_id
// onto the presentational topology (site → sector code).
// ---------------------------------------------------------------------------

export interface FieldRecord {
  id: string;
  name: string;
  farm_id?: string | null;
}

export interface SectorRecord {
  id: string;
  field_id: string;
  name: string;
}

/** A canonical sector id resolved to a presentational (site, sector-code). */
export interface SectorPlacement {
  siteName: string;
  sectorCode: string;
}

// ---------------------------------------------------------------------------
// Timestamp helpers (moved out of the component so tests can target them).
// ---------------------------------------------------------------------------

export function nodeTimestamp(node: IoTNode | undefined): number | null {
  if (!node) return null;
  const candidates = [node.last_seen_at, node.latest?.ts]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .map((value) => Date.parse(value))
    .filter((value) => Number.isFinite(value));
  return candidates.length > 0 ? Math.max(...candidates) : null;
}

export function isNodeOnline(node: IoTNode | undefined, now: number): boolean {
  const ts = nodeTimestamp(node);
  return ts !== null && now - ts < FRESHNESS_MS;
}

// ---------------------------------------------------------------------------
// Topology ↔ backend join (section 9). Never hardcoded: the association comes
// from the backend Field → Sector hierarchy + node.sector_id.
// ---------------------------------------------------------------------------

const normalizeForMatch = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const fieldMatchesSite = (fieldName: string, siteName: string): boolean => {
  const f = normalizeForMatch(fieldName);
  const s = normalizeForMatch(siteName);
  return f === s || f.includes(s) || s.includes(f);
};

const sectorMatchesCode = (sectorName: string, code: string): boolean => {
  const n = normalizeForMatch(sectorName);
  const c = normalizeForMatch(code);
  if (c.length === 0) return false;
  return n === c || n.endsWith(` ${c}`) || n === `sector ${c}` || n === `secteur ${c}`;
};

/**
 * Build the canonical ``sector_id → (siteName, sectorCode)`` map from the
 * backend Field/Sector records and the presentational topology.
 *
 * A sector id is mapped only when BOTH its field resolves to exactly one site
 * and its name resolves to exactly one sector code of that site. Ambiguous or
 * unmatched sector ids are simply absent from the map — a node carrying such an
 * id (or no id at all, e.g. the generic commissioning pack) is never placed
 * into a sector. This is the fail-closed guard against farm-scoped leakage.
 */
export function buildSectorPlacement(
  fields: FieldRecord[],
  sectors: SectorRecord[],
  sites: { name: string; sectors: { code: string }[] }[],
): Map<string, SectorPlacement> {
  const fieldNameById = new Map<string, string>();
  for (const field of fields) {
    if (field.id && field.name) fieldNameById.set(field.id, field.name);
  }

  const placement = new Map<string, SectorPlacement>();

  for (const sector of sectors) {
    const fieldName = fieldNameById.get(sector.field_id);
    if (!fieldName) continue;

    const matchedSites = sites.filter((site) => fieldMatchesSite(fieldName, site.name));
    if (matchedSites.length !== 1) continue;

    const site = matchedSites[0];
    const matchedCodes = site.sectors.filter((s) => sectorMatchesCode(sector.name, s.code));
    if (matchedCodes.length !== 1) continue;

    placement.set(sector.id, { siteName: site.name, sectorCode: matchedCodes[0].code });
  }

  return placement;
}

/**
 * Nodes whose canonical sector_id resolves to exactly ``(siteName, sectorCode)``.
 * Only these nodes may feed that sector's operational card or detail metrics.
 */
export function nodesForSector(
  nodes: IoTNode[],
  placement: Map<string, SectorPlacement>,
  siteName: string,
  sectorCode: string,
): IoTNode[] {
  return nodes.filter((node) => {
    const sectorId = node.sector_id;
    if (!sectorId) return false;
    const resolved = placement.get(sectorId);
    return resolved?.siteName === siteName && resolved.sectorCode === sectorCode;
  });
}

// ---------------------------------------------------------------------------
// Operational state derivation (section 6).
// ---------------------------------------------------------------------------

/**
 * Derive a sector's operational state from its sector-bound controller node.
 *
 *  - `not_commissioned` — no valve-capable node (no controller sector-bound).
 *  - `unknown`         — stale telemetry, missing timestamp, or a frame that
 *                        cannot be interpreted as a complete snapshot.
 *  - `active`          — fresh telemetry whose payload confirms a valve open.
 *  - `stopped`         — fresh telemetry whose payload confirms EVERY relevant
 *                        output closed (a complete 4-channel ASVWF01 frame).
 *
 * A partial all-closed frame, or a scalar "no valve open", is NOT a complete
 * photograph and therefore never becomes `stopped` (absence of OPEN ≠ STOPPED).
 */
export function deriveOperationalState(
  node: IoTNode | undefined,
  now: number,
): OperationalState {
  if (!node || !isValveCapable(node.model)) return 'not_commissioned';

  const ts = nodeTimestamp(node);
  if (ts === null) return 'unknown';
  if (now - ts >= FRESHNESS_MS) return 'unknown';

  const measures = node.latest?.measures;
  const channels = getValveChannels(measures);

  if (channels.length > 0) {
    if (channels.some((channel) => channel.open)) return 'active';
    // Complete snapshot: exactly the four operational ASVWF01 valve channels.
    const distinctChannels = new Set(channels.map((channel) => channel.channel));
    if (distinctChannels.size === 4) return 'stopped';
    // Partial frame with every reported channel closed — incomplete, so unknown.
    return 'unknown';
  }

  // No strict canonical channels: a scalar open signal is still a reliable
  // "active", but a scalar closed is not a complete "stopped" proof.
  const scalar = getValveState(measures);
  if (scalar === 1) return 'active';

  return 'unknown';
}

// ---------------------------------------------------------------------------
// "Since when" derivation (section 7). Optional — depends on telemetry history.
// ---------------------------------------------------------------------------

export interface TelemetryPoint {
  ts?: string | null;
  measures?: Measures | null;
}

type PointState = 'open' | 'closed' | 'unknown';

/** Generic binary point state for a proven-transition walk. */
export type BinaryPointState = 'on' | 'off' | 'unknown';

/**
 * Walk a chronological series backwards to the most recent PROVEN transition
 * into `target`, and return how long ago it happened (ms), or `null`.
 *
 * This is the single implementation of "depuis quand" for every binary signal
 * (valve channels, relay contacts). It is deliberately strict:
 *
 *   - the LAST point must already be in `target`, otherwise the series does not
 *     describe the current state at all;
 *   - the run of `target` must not reach the start of the window — that means
 *     no transition was observed, only the window boundary;
 *   - the point immediately preceding the run must be the explicit OPPOSITE
 *     state. An `unknown` predecessor does NOT prove a transition (the signal
 *     may have been in `target` all along, unreported).
 *
 * Every rejection yields `null`, which the UI renders as "durée inconnue" —
 * never a duration inferred from packet age.
 */
export function deriveRunSince<T>(
  series: T[],
  pointState: (item: T) => BinaryPointState,
  timestampOf: (item: T) => string | null | undefined,
  target: 'on' | 'off',
  now: number,
): number | null {
  if (series.length === 0) return null;
  const opposite: BinaryPointState = target === 'on' ? 'off' : 'on';

  const states = series.map(pointState);
  if (states[states.length - 1] !== target) return null;

  let runStart = states.length - 1;
  while (runStart > 0 && states[runStart - 1] === target) runStart -= 1;

  if (runStart === 0) return null;
  if (states[runStart - 1] !== opposite) return null;

  const sinceTs = Date.parse(timestampOf(series[runStart]) ?? '');
  if (!Number.isFinite(sinceTs)) return null;
  const duration = now - sinceTs;
  return duration >= 0 ? duration : null;
}

/** Per-point state, using the same interpretation as deriveOperationalState. */
function pointOpenState(measures: Measures | null | undefined): PointState {
  const m = measures ?? undefined;
  const channels = getValveChannels(m);
  if (channels.length > 0) {
    if (channels.some((channel) => channel.open)) return 'open';
    const distinctChannels = new Set(channels.map((channel) => channel.channel));
    if (distinctChannels.size === 4) return 'closed';
    return 'unknown';
  }
  const scalar = getValveState(m);
  if (scalar === 1) return 'open';
  return 'unknown';
}

/**
 * Derive how long a sector has been in ``currentState`` (active/stopped) from a
 * chronological telemetry series. Returns the duration in ms, or ``null`` when
 * the series cannot prove a transition — never a fabricated "since X".
 *
 * The last point must match ``currentState``; we then walk backwards to the
 * most recent transition. If the run reaches the window start (no transition
 * observed) or the timestamp is unparseable, the answer is ``null``.
 */
export function deriveStateSince(
  series: TelemetryPoint[],
  currentState: 'active' | 'stopped',
  now: number,
): number | null {
  // Valve semantics mapped onto the shared binary walk: open ↔ on.
  const asBinary = (point: TelemetryPoint): BinaryPointState => {
    const state = pointOpenState(point.measures);
    if (state === 'open') return 'on';
    if (state === 'closed') return 'off';
    return 'unknown';
  };
  return deriveRunSince(
    series,
    asBinary,
    (point) => point.ts,
    currentState === 'active' ? 'on' : 'off',
    now,
  );
}

/**
 * The freshest valve-capable (controller) node in a set, or ``undefined``.
 * A sector's operational state is interpreted from its single freshest
 * controller — never summed across controllers belonging to another sector.
 */
export function freshestValveNode(nodes: IoTNode[]): IoTNode | undefined {
  let best: IoTNode | undefined;
  let bestTs = Number.NEGATIVE_INFINITY;
  for (const node of nodes) {
    if (!isValveCapable(node.model)) continue;
    const ts = nodeTimestamp(node) ?? Number.NEGATIVE_INFINITY;
    if (!best || ts > bestTs) {
      best = node;
      bestTs = ts;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Sector-scoped metrics (section 8).
// ---------------------------------------------------------------------------

export interface ScopedMetric {
  value: number;
  nodeId: string;
}

/**
 * Pick the freshest valid value produced by a set of sector-scoped nodes,
 * keeping the source node identifiable. No arbitrary averaging; a value with no
 * timestamp only wins when no dated candidate exists.
 */
export function freshestScopedMetric(
  nodes: IoTNode[],
  getter: (measures: Measures | undefined) => number | undefined,
): ScopedMetric | undefined {
  let best: { value: number; nodeId: string; ts: number } | undefined;

  for (const node of nodes) {
    const value = getter(node.latest?.measures);
    if (value === undefined || !Number.isFinite(value)) continue;
    const ts = nodeTimestamp(node) ?? Number.NEGATIVE_INFINITY;
    if (!best || ts > best.ts) {
      best = { value, nodeId: node.id, ts };
    }
  }

  return best ? { value: best.value, nodeId: best.nodeId } : undefined;
}
